import { useCallback } from "react";
import { useTeamManagement } from "./useTeamManagement";
import { useSelector } from "react-redux";
import {
  selectTeamMembers,
  selectAvailableRoles,
  selectTotalMembers,
  selectActiveMembers,
  selectInactiveMembers,
  selectHasAnyLoadingState,
  selectHasAnyError,
} from "../../redux/selectors/teamSelectors";

export const useRolesAndPermissions = () => {
  const teamManagement = useTeamManagement();

  //Todo => Selectors
  const teamMembers = useSelector(selectTeamMembers);
  const availableRoles = useSelector(selectAvailableRoles);
  const totalMembers = useSelector(selectTotalMembers);
  const activeMembers = useSelector(selectActiveMembers);
  const inactiveMembers = useSelector(selectInactiveMembers);
  const hasAnyLoading = useSelector(selectHasAnyLoadingState);
  const hasAnyError = useSelector(selectHasAnyError);

  //Todo => Helper functions
  const getTeamMemberById = useCallback(
    (id) => {
      return teamMembers.find((member) => member.id === id);
    },
    [teamMembers]
  );

  const getTeamMembersByRole = useCallback(
    (role) => {
      return teamMembers.filter(
        (member) => member.role.toLowerCase() === role.toLowerCase()
      );
    },
    [teamMembers]
  );

  const getActiveTeamMembers = useCallback(() => {
    return teamMembers.filter((member) => member.status === "Active");
  }, [teamMembers]);

  const getRolePermissions = useCallback(
    (roleValue) => {
      const role = availableRoles.find((r) => r.value === roleValue);
      return role ? role.permissions : [];
    },
    [availableRoles]
  );

  const canUserPerformAction = useCallback(
    (userId, module, action) => {
      const member = getTeamMemberById(userId);
      if (!member) return false;

      const rolePermissions = getRolePermissions(member.role);

      const modulePermissionSet = rolePermissions.find(
        (perm) => perm.name === module
      );
      return modulePermissionSet?.permissions?.includes(action) || false;
    },
    [getTeamMemberById, getRolePermissions]
  );

  const getUserPermissionsByModule = useCallback(
    (userId) => {
      const member = getTeamMemberById(userId);
      if (!member) return {};

      const rolePermissions = getRolePermissions(member.role);

      const permissionsByModule = {};
      rolePermissions.forEach((module) => {
        permissionsByModule[module.name] = module.permissions;
      });

      return permissionsByModule;
    },
    [getTeamMemberById, getRolePermissions]
  );

  //Todo => Team statistics
  const getTeamStatistics = useCallback(() => {
    const adminCount = getTeamMembersByRole("admin").length;
    const managerCount = getTeamMembersByRole("manager").length;
    const employeeCount = getTeamMembersByRole("employee").length;

    return {
      total: totalMembers,
      active: activeMembers,
      inactive: inactiveMembers,
      adminCount,
      managerCount,
      employeeCount,
      activePercentage:
        totalMembers > 0 ? Math.round((activeMembers / totalMembers) * 100) : 0,
    };
  }, [totalMembers, activeMembers, inactiveMembers, getTeamMembersByRole]);

  //Todo => Advanced team operations
  const bulkUpdateStatus = useCallback(
    async (userIds, status) => {
      const promises = userIds.map((id) =>
        teamManagement.updateTeamMemberStatus({ userId: id, status })
      );

      try {
        await Promise.all(promises);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [teamManagement]
  );

  const searchTeamMembers = useCallback(
    (searchTerm) => {
      if (!searchTerm) return teamMembers;
      const term = searchTerm.toLowerCase();
      return teamMembers.filter(
        (member) =>
          member.fullName?.toLowerCase().includes(term) ||
          member.email?.toLowerCase().includes(term) ||
          member.phone?.includes(term) ||
          member.role?.toLowerCase().includes(term)
      );
    },
    [teamMembers]
  );

  const filterTeamMembers = useCallback(
    (filters) => {
      let filtered = teamMembers;

      if (filters.status) {
        filtered = filtered.filter(
          (member) => member.status === filters.status
        );
      }

      if (filters.role) {
        filtered = filtered.filter(
          (member) => member.role.toLowerCase() === filters.role.toLowerCase()
        );
      }

      if (filters.dateRange) {
        //Todo => Filter by creation date range
        const { start, end } = filters.dateRange;
        filtered = filtered.filter((member) => {
          const createdDate = new Date(member.createdOn);
          return createdDate >= start && createdDate <= end;
        });
      }

      return filtered;
    },
    [teamMembers]
  );

  //Todo => Validation helpers
  const validateTeamMemberData = useCallback(
    (userData) => {
      const errors = {};

      if (!userData.fullName?.trim()) {
        errors.fullName = "Full name is required";
      }

      if (!userData.email?.trim()) {
        errors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
        errors.email = "Email is invalid";
      }

      if (!userData.phone?.trim()) {
        errors.phone = "Phone number is required";
      }

      if (!userData.role) {
        errors.role = "Role is required";
      }

      //Todo => Check for duplicate email
      const existingMember = teamMembers.find(
        (member) => member.email === userData.email && member.id !== userData.id
      );
      if (existingMember) {
        errors.email = "Email already exists";
      }

      return {
        isValid: Object.keys(errors).length === 0,
        errors,
      };
    },
    [teamMembers]
  );

  return {
    //Todo => Data
    teamMembers,
    availableRoles,

    //Todo => Statistics
    totalMembers,
    activeMembers,
    inactiveMembers,
    statistics: getTeamStatistics(),

    //Todo => Loading states
    isLoading: teamManagement.isLoading,
    isFetchingMembers: teamManagement.isFetchingMembers,
    isFetchingRoles: teamManagement.isFetchingRoles,
    isCreating: teamManagement.isCreating,
    isUpdating: teamManagement.isUpdating,
    isDeleting: teamManagement.isDeleting,
    hasAnyLoading,
    hasAnyError,

    //Todo => Actions
    createTeamMember: teamManagement.createTeamMember,
    updateTeamMember: teamManagement.updateTeamMember,
    deleteTeamMember: teamManagement.deleteTeamMember,
    updateTeamMemberStatus: teamManagement.updateTeamMemberStatus,

    //Todo =>  Helper functions
    getTeamMemberById,
    getTeamMembersByRole,
    getActiveTeamMembers,
    getRolePermissions,
    canUserPerformAction,
    getUserPermissionsByModule,

    //Todo => Advanced operations
    bulkUpdateStatus,
    searchTeamMembers,
    filterTeamMembers,
    validateTeamMemberData,

    //Todo => Utilities
    refreshTeamData: teamManagement.refreshTeamData,
    clearAllErrors: teamManagement.clearAllErrors,
    refetchMembers: teamManagement.refetchMembers,
  };
};
